"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useRef } from "react";
export type ScrollDirection = "up" | "down" | "none";
export interface UseScrollDirectionOptions {
  /** Minimum scroll distance in pixels before direction changes */
  threshold?: number;
}
export interface UseScrollDirectionReturn {
  /** Current scroll direction */
  direction: ScrollDirection;
  /** Whether the page is scrolled to the top */
  isAtTop: boolean;
  /** Current scroll position in pixels */
  scrollY: number;
}

/**
 * Hook to detect scroll direction and position
 *
 * Uses requestAnimationFrame for performance and includes a threshold
 * to prevent jittery behavior on small scroll movements.
 *
 * @param options - Configuration options
 * @returns Object with current scroll direction, top position state, and scroll position
 *
 * @example
 * ```tsx
 * const { direction, isAtTop } = useScrollDirection({ threshold: 10 });
 * // Hide header when scrolling down
 * <header className={direction === 'down' ? 'hidden' : 'visible'} />
 * ```
 */
export function useScrollDirection(t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    threshold: t2
  } = options;
  const threshold = t2 === undefined ? 10 : t2;
  const [direction, setDirection] = useState("none");
  const [isAtTop, setIsAtTop] = useState(_temp);
  const [scrollY, setScrollY] = useState(_temp2);
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const ticking = useRef(false);
  let t3;
  let t4;
  if ($[2] !== threshold) {
    t3 = () => {
      const handleScroll = () => {
        if (!ticking.current) {
          window.requestAnimationFrame(() => {
            const currentScrollY = window.scrollY;
            setIsAtTop(currentScrollY < 10);
            const scrollDiff = currentScrollY - lastScrollY.current;
            if (Math.abs(scrollDiff) > threshold) {
              if (scrollDiff > 0 && currentScrollY > 50) {
                setDirection("down");
              } else {
                if (scrollDiff < 0) {
                  setDirection("up");
                }
              }
            }
            setScrollY(currentScrollY);
            lastScrollY.current = currentScrollY;
            ticking.current = false;
          });
          ticking.current = true;
        }
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [threshold];
    $[2] = threshold;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== direction || $[6] !== isAtTop || $[7] !== scrollY) {
    t5 = {
      direction,
      isAtTop,
      scrollY
    };
    $[5] = direction;
    $[6] = isAtTop;
    $[7] = scrollY;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function _temp2() {
  return typeof window !== "undefined" ? window.scrollY : 0;
}
function _temp() {
  return typeof window !== "undefined" ? window.scrollY < 10 : true;
}